import cn from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';

import {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE,
} from '@/shared/config';
import { useAppDispatch, useMedia } from '@/shared/hooks';
import { Checkbox, Icon, Skeleton } from '@/shared/ui';

import { assetsModel } from '@/entities/assets';
import { useCheckReportSignature } from '@/entities/reports';
import { slicesModel } from '@/entities/slices';

import { INITIAL_WIDTH, type MedicalImageProps } from '../../config';

import styles from './MedicalImage.module.scss';

export const MedicalImage: FC<MedicalImageProps> = (props) => {
  const {
    medicalImage,
    canUserDeleteSlices,
    className,
    draggable = false,
    focused,
    imageClassName,
    imageOverlay,
    isImgSelected,
    medicalImageLabel,
    order,
    selectable,
    selfPaintSrc,
    toggled,
    onImageClick,
    onImageReady,
    onRemoveMedicalImage,
    toggleSelected,
  } = props;

  const dispatch = useAppDispatch();

  const { isMobile } = useMedia();

  const imageHeight = isMobile
    ? DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE
    : DEFAULT_MEDICAL_IMAGE_HEIGHT;

  const size = {
    w: INITIAL_WIDTH,
    h: imageHeight,
  };

  const [isImageReady, setIsImageReady] = useState(false);
  const [dragging, setDragging] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const resolvedSelfPaintSrc =
    selfPaintSrc ??
    (medicalImage.src?.startsWith('data:') ? medicalImage.src : undefined);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const onReady = () => setIsImageReady(true);

    if (canvas.getAttribute('ready') === 'true') {
      onReady();
    }

    canvas.addEventListener('ready', onReady);

    return () => canvas.removeEventListener('ready', onReady);
  }, []);

  useEffect(() => {
    if (isImageReady) {
      onImageReady?.(medicalImage.assetID);
    }
  }, [isImageReady, medicalImage.assetID, onImageReady]);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!resolvedSelfPaintSrc || !canvas) {
      return;
    }

    const image = new Image();

    image.onload = () => {
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext('2d')?.drawImage(image, 0, 0);
      canvas.setAttribute('ready', 'true');
      canvas.dispatchEvent(new Event('ready'));
    };
    image.src = resolvedSelfPaintSrc;

    return () => {
      image.onload = null;
    };
  }, [resolvedSelfPaintSrc]);

  const { checkReportSignature } = useCheckReportSignature();

  const handleDeleteMedicalImage = async (
    e: React.MouseEvent<SVGSVGElement>,
  ) => {
    e.stopPropagation();

    const deleteImage = async () => {
      if (onRemoveMedicalImage) {
        onRemoveMedicalImage(medicalImage);
      }

      if (canUserDeleteSlices && medicalImage.groupName === 'UserUpload') {
        await dispatch(
          assetsModel.thunks.deleteAsset({
            AssetID: medicalImage.assetID,
          }),
        );
      }
    };

    if (isImgSelected) {
      checkReportSignature({
        onSignatureChecked: deleteImage,
      });
    } else {
      await deleteImage();
    }
  };

  const handleDragStart = () => {
    dispatch(slicesModel.actions.setDraggedSliceID(medicalImage.assetID));

    setDragging(true);
  };
  const handleDragEnd = () => {
    dispatch(slicesModel.actions.setDraggedSliceID(''));

    setDragging(false);
  };

  return (
    <div
      data-component="medical-image"
      data-group={`${medicalImage.groupName}`}
      className={cn(styles.container, className, focused && styles.focused)}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        className={cn(styles.imageContainer, imageClassName)}
        style={{ height: imageHeight }}
        onClick={onImageClick}
      >
        {!isImageReady && (
          <Skeleton
            width={toggled ? size.w / 2 : size.w}
            borderRadius="12px"
            height={imageHeight}
          />
        )}

        <canvas
          id={medicalImage.assetID}
          ref={canvasRef}
          style={{
            height: imageHeight,
            ...(resolvedSelfPaintSrc && { pointerEvents: 'none' }),
          }}
          className={styles.medicalImage}
        />

        {(onRemoveMedicalImage || canUserDeleteSlices) && !dragging && (
          <div className={styles.removeButtonContainer}>
            <Icon
              name="delete"
              size={toggled ? 16 : 24}
              onClick={handleDeleteMedicalImage}
            />
          </div>
        )}
      </div>

      {imageOverlay}

      {medicalImageLabel && (
        <p className={cn(styles.medicalImageLabel, 'p2')}>
          {medicalImageLabel}
        </p>
      )}

      {(selectable ?? draggable) && (
        <div className={styles.selectSliceControl}>
          <Checkbox
            boxClassName={styles.selectCheckbox}
            checked={isImgSelected || false}
            onChange={() => {
              if (toggleSelected) {
                toggleSelected(medicalImage);
              }
            }}
          />
        </div>
      )}

      {typeof order === 'number' && order >= 0 ? (
        <div
          className={cn(styles.numeration, isImgSelected && styles.selected)}
        >
          {isImgSelected && <Icon name="check" size={16} />}
          <span className={styles.order}>{order + 1}</span>
        </div>
      ) : null}

      <div className={styles.orientWrapper}>
        <div className={styles.orientContainer}>
          <span className={styles.medicalImageText}>
            {medicalImage?.leftSideOrient}
          </span>
          <span className={styles.medicalImageText}>
            {medicalImage?.rightSideOrient}
          </span>
        </div>
      </div>
    </div>
  );
};
